import { ClosetUnit, Room, Wall } from '../domain';
import { shuffle } from '../utils';
import { neighbors } from '../control-flow/neighbors';
import { pairwise } from '../control-flow/pairwise';
import { OptionalProperties, update } from "../json/optional-properties";
import { angleBetweenWalls, wallLength } from './wall-controllers';

export enum Gravity {
    LEFT,
    RIGHT,
    CENTER,
    NONE,
}

export const gravityFromString = (gravity: string): Gravity => {
    if(!gravity) return Gravity.NONE;
    switch(gravity.toUpperCase()) {
        case "LEFT":
            return Gravity.LEFT;
        case "RIGHT":
            return Gravity.RIGHT;
        case "CENTER":
            return Gravity.CENTER;
        case "NONE":
        default:
            return Gravity.NONE;
    }
}

/**
 * Divides the width into a maximal number of spaces having a preferred width,
 * with the remainder split between two units on either end.
 */
const divide = (pref: number, width: number): Unit[] => {
    let count = Math.floor(width / pref);
    while ((count*pref + 0.75*count) > width) {
        count -= 1
    }
    if(count > 0) count -= 1;

    if(count == 0) {
        return [new Unit(width)];
    }

    const remainder = width - (count*pref + 0.75*count) - 0.75;

    const
        ends = [remainder/2, remainder/2],
        units = Array(count);

    for(let i=0; i < units.length; i++) {
        units[i] = new Unit(pref);
    }

    roundWithErrorCorrection(ends);
    units.push(new Unit(ends[1], Gravity.RIGHT));
    units.unshift(new Unit(ends[0], Gravity.LEFT));

    return units;
}

/**
 * Divides the remaining space evenly into spaces no larger than the given
 * maximum width, taking into account a gap for the dividers between spaces.
 * The results are rounded to the nearest 1/16th of an inch such that the sum
 * of the output should be within 1/16th of the supplied width.
 */
const divideEvenly = (width: number, max: number = 24, gap: number = 0.75)
    : number[] =>
{
    let
        i = 1,
        unit = (width - gap*i) / i;
    while(unit > max) {
        i += 1;
        unit = (width - gap*i) / i;
    }
    
    const result = new Array(i);
    result.fill(unit);
    roundWithErrorCorrection(result);
    return result;
}

/**
 * If you round a series of values that represent subdivisions of a space, you
 * can round down several times in a row and so you shrink the overall length.
 * This procedure tracks the cumulative error to achieve a better overall
 * adherence to the total length.
 */
const roundWithErrorCorrection = (values: number[], denomenator: number = 16) => {
    const
        n = denomenator,
        d = 1/denomenator;
    let error = 0;
    for(let i=0; i < values.length; i++) {
        const
            original = values[i],
            adjusted = original - error,
            rounded = Math.round(adjusted * n) * d;
        error += rounded - original;
        values[i] = rounded;
    }
}

// Test of roundWithErrorCorrection (eliminated by rollup due to `if`)
if(false) {
    const results: object[] = [];
    for(let j=0; j < 10; j++) {

        const randos = new Array(4);
        const value = 20 + Math.random();
        randos.fill(value);
        const originalTotal = randos[0]*randos.length;
        const simplyRoundedTotal =
            randos.reduce((a, b) => Math.round(a*16)/16 + Math.round(b*16)/16, 0);
        roundWithErrorCorrection(randos);
        const correctedTotal = randos.reduce((a, b) => a + b, 0);

        const simpleError = originalTotal - simplyRoundedTotal;
        const correctedError = originalTotal - correctedTotal;
        let improved = "yes";
        if(Math.abs(simpleError) == Math.abs(correctedError)) improved = "wash";
        else if(Math.abs(simpleError) < Math.abs(correctedError)) improved = "no";

        results.push({
            scenario: `${randos.length} x ${value}`,
            total: Number(originalTotal.toFixed(4)),
            simple: simplyRoundedTotal,
            "simple error": Number((originalTotal - simplyRoundedTotal).toFixed(4)),
            corrected: correctedTotal,
            "corrected error": Number((originalTotal - correctedTotal).toFixed(4)),
            "improved ?": improved,
        });
    }
    console.table(results);
}

/**
 * These units are an intermediate data structure that we use for 
 */
class Unit {
    width: number;
    gravity: Gravity;

    constructor(width: number, gravity: Gravity = Gravity.NONE) {
        this.width = width;
        this.gravity = gravity;
    }
}

/**
 * This is an intermediate data structure that holds some information we need
 * while 
 */
class PreWall {
    target: Wall;

    prev: PreWall | null;
    next: PreWall | null;

    /** Total length of the wall */
    total: number;

    /** Remaining available space on the wall */
    available: number;

    /** "all", "fill", or "none", as with the domain model */
    prefill: typeof Wall.prototype.prefill;

    /** Is there a filler unit at the start of the wall? */
    fillerStart: boolean;

    /** Is there a filler unit at the end of the wall? */
    fillerEnd: boolean;

    constructor(options: OptionalProperties<PreWall>) {
        update(this, options);
    }
}

/**
 * Re-order the given units (in place) according to their gravity. If the 
 * stable parameter is false, then each section (left, right, center) will be
 * shuffled.
 */
const arrange = (units: Unit[], stable: boolean = true) => {
    const
        lefts: Unit[] = [],
        rights: Unit[] = [],
        centers: Unit[] = [],
        nones: Unit[] = [];
    for(const u of units) {
        switch(u.gravity) {
            case Gravity.LEFT:
                lefts.push(u);
                break;
            case Gravity.RIGHT:
                rights.push(u);
                break;
            case Gravity.CENTER:
                centers.push(u);
                break;
            case Gravity.NONE:
            default:
                u.gravity = Gravity.NONE;
                nones.push(u);
        }
    }

    if(!stable) {
        shuffle(lefts);
        shuffle(rights);
        shuffle(centers);
        shuffle(nones);
    }

    units.length = 0;
    units.push(...centers);

    let i=0;
    while(i < nones.length) {
        if(i % 2 == 0) {
            units.push(nones[i]);
        } else {
            units.unshift(nones[i]);
        }
        i++;
    }

    units.splice(0, 0, ...lefts);
    units.push(...rights);
}

const setPrevFillers = (wall: PreWall) => {
    if(wall.prev) {
        const angle = angleBetweenWalls(wall.prev.target, wall.target);
        if(wall.prefill != 'none'
           && wall.prev.prefill == 'fill'
           && angle > 0 && angle < Math.PI)
        {
            wall.prev.fillerEnd = true;
        }
        setPrevFillers(wall.prev);
    }

}

const setNextFillers = (wall: PreWall) => {
    if(wall.next) {
        const angle = angleBetweenWalls(wall.target, wall.next.target);
        if(wall.prefill != 'none'
           && wall.next.prefill == 'fill'
           && angle > 0 && angle < Math.PI)
        {
            wall.next.fillerStart = true;
        }
        setNextFillers(wall.next);
    }
}

/**
 * @param thickness - The thickness of the vertical panels around the available
 * space.
 */
export const distributeFillerUnits =
    (walls: Wall[], fillerWidth: number, thickness: number = 0.75): PreWall[] =>
{
    if(walls.length == 0) return [];

    // The first part of the task is to decide on how the filler units are
    // added to the corners. We use the PreWall class and its fillerStart and
    // fillerEnd properties to work through this decision.

    // Create a pre-wall from each wall in the room
    const prewalls: PreWall[] = walls.map(wall => {
        const length = wallLength(wall);
        return new PreWall({
            target: wall,
            total: length,
            available: length,
            prefill: wall.prefill,
        })
    });

    let
        max: PreWall = prewalls[0],
        anyMarkedAll: boolean = false;

    for(const [prev, wall, next] of neighbors(prewalls)) {
        if(wall.prefill != 'none' && max.total < wall.total) {
            max = wall;
        }
        if(wall.prefill == 'all') {
            anyMarkedAll = true;
        }
        // Link them into a doubly linked list
        wall.prev = prev;
        wall.next = next;
    }

    if(!anyMarkedAll) {
        max.prefill = 'all';
    }


    // If there are any 'all' walls, then we recursively set the fillerStart 
    // and fillerEnd of the adjacent walls. This can result in void spaces
    // between fillers, which we'll correct afterwards.
    for(const wall of prewalls) {
        if(wall.prefill == 'all') {
            setPrevFillers(wall);
            setNextFillers(wall);
        }
    }

    // Finally, for two adjacent walls, if they have adjacent filler units,
    // then remove the filler from the shorter wall
    for(const [a, b] of pairwise(prewalls)) {
        if(a.fillerEnd && b.fillerStart) {
            if(a.total > b.total) {
                a.fillerEnd = true;
                b.fillerStart = false;
            } else {
                a.fillerEnd = false;
                b.fillerStart = true;
            }
        }

        // Anything that is immediately before an island should not have a
        // filler unit on the end because it's necessarily the last wall in the
        // chain of connected walls.
        if(!a.target.island && b.target.island) {
            a.fillerEnd = false;
        }
    }

    for(const wall of prewalls) {

        // everything skips these, so they don't need the avialable space
        if(wall.prefill == 'none') continue;

        if(wall.prefill == 'all') {
            wall.available = wall.total  - 2*thickness;
        } else {
            // in this case wall.filler must be 'fill'

            let fillerSpace = 0;
            if(wall.fillerStart) {
                fillerSpace += wall.prev.target.depth + fillerWidth + thickness; //26.75;
            } else {
                wall.available -= thickness;
            }
            if(wall.fillerEnd) {
                fillerSpace += wall.next.target.depth + fillerWidth + thickness; //26.75;
            } else {
                wall.available -= thickness;
            }

            wall.available -= fillerSpace;

            // If we don't have room left for a filler, then we'll skip this wall
            if(wall.available - fillerSpace < fillerWidth) {
                wall.prefill = 'none';
                continue;
            }
        }
    }
    return prewalls;
}

/**
 * This function takes the output of distributeFillerUnits, an array of PreWall
 * instances, and updates their corresponding Wall models' `prefill` properties
 * to reflect the presence/absence of filler units.
 */
export const updatePrefillProperties = (prewalls: PreWall[]) => {
    for(const pwall of prewalls) {
        if(pwall.fillerStart && pwall.fillerEnd) {
            pwall.target.prefill = 'fill-both';
        } else if(pwall.fillerStart) {
            pwall.target.prefill = 'fill-start';
        } else if(pwall.fillerEnd) {
            pwall.target.prefill = 'fill-end';
        } else if(pwall.target.prefill != 'none') {
            pwall.target.prefill = 'all';
        }
    }
}

export const prefillRoom = (room: Room, fillerDepth: number) => {

    const prewalls = distributeFillerUnits(room.walls, fillerDepth);

    // Now we can convert the PreWalls into lists of Units and then finally
    // into actual ClosetUnit instances on the Wall.
    for(const wall of prewalls) {
        const units = [];

        if(wall.prefill == 'none') continue;

//        const widths = divideEvenly(wall.available);
//        for(const w of widths) {
//            units.push(new Unit(w));
//        }
        units.push(...divide(24, wall.available));

        // Arrange the units by their gravity property
        arrange(units);

        // Add the filler units after arrangement
        if(wall.fillerStart) {
            units.unshift(new Unit(12, Gravity.LEFT));
        }
        if(wall.fillerEnd) {
            units.push(new Unit(12, Gravity.RIGHT));
        }

        // Position
        let x: number = 0.75;
        if(wall.fillerStart) {
            x = 14;
        }
        for(const unit of units) {
            wall.target.closetUnits.push(ClosetUnit.fromJSON({
                width: unit.width,
                depth: 14,
                position: {x},
            }));
            x += unit.width + 0.75;
        }
    }
    room.commit(true);
}

export const clearAndPrefillRoom = (room: Room, fillerWidth: number) => {
    for(const wall of room.walls) {
        wall.closetUnits.length = 0;
    }
    prefillRoom(room, fillerWidth);
}
